import { useCallback, useMemo, useState } from 'react';
import { withNamespaces } from 'react-i18next';
import ReactFlow, {
    Controls,
    ReactFlowProvider,
    useReactFlow,
} from 'react-flow-renderer';
import {
    Button,
    Card,
    CardBody,
} from 'reactstrap';

import { SIMULATION_STATUS } from '../hooks/useSimulationClock';
import { FLOW_STATES } from '../mock/flowDefinition';
import AnalysisNode from './nodes/AnalysisNode';
import BidSourceNode from './nodes/BidSourceNode';
import BranchNode from './nodes/BranchNode';
import DecisionNode from './nodes/DecisionNode';
import JunctionNode from './nodes/JunctionNode';
import StageNode from './nodes/StageNode';
import getStateVisuals, { FLOW_STATE_LEGEND } from './nodes/stateVisuals';
import VerificationSignals from './VerificationSignals';

const nodeTypes = {
    analysis: AnalysisNode,
    bidSource: BidSourceNode,
    branch: BranchNode,
    decision: DecisionNode,
    junction: JunctionNode,
    stage: StageNode,
};

const TRANSITION = 'opacity 150ms ease-in-out';
const DIMMED_OPACITY = 0.25;
const DEPENDENCY_OPACITY = 0.7;
const SHARED_DATA_RESULT_STATES = [
    FLOW_STATES.completed,
    FLOW_STATES.warning,
];
const ACTIVE_STAGE_STATES = [
    FLOW_STATES.processing,
    FLOW_STATES.completed,
    FLOW_STATES.warning,
    FLOW_STATES.failed,
];
const BASE_EDGE = {
    stroke: '#c5c9d4',
    strokeDasharray: '6 4',
    strokeWidth: 2,
};

const startLabels = {
    [SIMULATION_STATUS.finished]: 'Replay',
    [SIMULATION_STATUS.paused]: 'Resume',
};

const BidVerificationFlowCanvas = ({
    bid,
    getEdgeTrails,
    liveBranchId,
    nodes,
    onPause,
    onReset,
    onStart,
    relationships,
    showHeader = true,
    status,
    t,
}) => {
    const [hoveredBranchId, setHoveredBranchId] = useState(null);
    const reactFlowInstance = useReactFlow();
    const isRunning = status === SIMULATION_STATUS.running;
    const isFinished = status === SIMULATION_STATUS.finished;

    const expandedBranchIds = useMemo(() => {
        const branchIds = new Set();

        if (hoveredBranchId) {
            branchIds.add(hoveredBranchId);
        }

        if (!isFinished && liveBranchId) {
            branchIds.add(liveBranchId);
        }

        if (isRunning) {
            nodes.forEach((node) => {
                if (
                    node.role === 'stage'
                    && node.branchId
                    && ACTIVE_STAGE_STATES.includes(node.state)
                ) {
                    branchIds.add(node.branchId);
                }
            });
        }

        return branchIds;
    }, [
        hoveredBranchId,
        isFinished,
        isRunning,
        liveBranchId,
        nodes,
    ]);

    const isBranchExpanded = useCallback(
        (branchId) => !!(branchId && expandedBranchIds.has(branchId)),
        [expandedBranchIds]
    );

    const branchIdByNodeId = useMemo(() => (
        nodes.reduce((branchIds, node) => (
            node.branchId
                ? { ...branchIds, [node.id]: node.branchId }
                : branchIds
        ), {})
    ), [nodes]);

    // Source stages that feed an expanded category, or are fed by it.
    const dependencyStageIds = useMemo(() => {
        if (!expandedBranchIds.size) return [];

        return relationships
            .filter((relationship) => relationship.dependencyType === 'data-dependency')
            .reduce((stageIds, relationship) => {
                const sourceBranch = relationship.sourceBranch
                    || branchIdByNodeId[relationship.source];
                const targetBranch = relationship.targetBranch
                    || branchIdByNodeId[relationship.target];

                const touchesExpandedBranch = [sourceBranch, targetBranch].some(
                    (branchId) => isBranchExpanded(branchId)
                );

                if (!touchesExpandedBranch) {
                    return stageIds;
                }

                const relatedStageIds = [relationship.source, relationship.sourceStage]
                    .filter((nodeId) => nodeId && nodes.some((node) => (
                        node.id === nodeId && node.role === 'stage'
                    )));

                return [...stageIds, ...relatedStageIds];
            }, []);
    }, [
        branchIdByNodeId,
        expandedBranchIds,
        isBranchExpanded,
        nodes,
        relationships,
    ]);

    const flowNodes = useMemo(() => (
        nodes.map((node) => {
            const isStage = node.role === 'stage';
            const isDependencyStage = dependencyStageIds.includes(node.id);
            const isVisible = !isStage
                || isBranchExpanded(node.branchId)
                || isDependencyStage;
            const isDimmed = !!hoveredBranchId
                && !!node.branchId
                && node.branchId !== hoveredBranchId;

            return {
                id: node.id,
                type: node.type,
                position: node.position,
                data: {
                    ...node,
                    bidStatus: bid.status,
                    freight: bid.freight,
                },
                selectable: false,
                style: {
                    opacity: isDependencyStage
                        ? DEPENDENCY_OPACITY
                        : (isVisible ? (isDimmed ? DIMMED_OPACITY : 1) : 0),
                    pointerEvents: isVisible ? 'all' : 'none',
                    transition: TRANSITION,
                },
            };
        })
    ), [
        bid.freight,
        bid.status,
        dependencyStageIds,
        hoveredBranchId,
        isBranchExpanded,
        nodes,
    ]);

    const flowEdges = useMemo(() => (
        relationships.map((relationship) => {
            const relatedBranchIds = [
                relationship.branchId,
                relationship.sourceBranch,
                relationship.targetBranch,
                branchIdByNodeId[relationship.source],
                branchIdByNodeId[relationship.target],
            ].filter((branchId, index, branchIds) => (
                branchId && branchIds.indexOf(branchId) === index
            ));
            const isBranchScoped = relatedBranchIds.length > 0;
            const isDependency = relationship.dependencyType === 'data-dependency';
            const isActiveBranch = relatedBranchIds.some((branchId) => isBranchExpanded(branchId));
            const isRelatedHover = !!hoveredBranchId
                && relatedBranchIds.includes(hoveredBranchId);
            const isSharedDataVisible = isDependency && (
                isFinished
                    ? isRelatedHover
                    : (
                        isRelatedHover
                        || SHARED_DATA_RESULT_STATES.includes(relationship.state)
                    )
            );
            // While a branch is expanded its checks carry the flow, so the
            // summary shortcut steps back.
            const isVisible = relationship.dependencyType === 'data-dependency'
                ? isSharedDataVisible
                : relationship.dependencyType === 'branch-summary'
                    ? !isActiveBranch
                    : relationship.level === 'primary' || isActiveBranch;
            const isDimmed = !!hoveredBranchId
                && isBranchScoped
                && !relatedBranchIds.includes(hoveredBranchId);
            const opacity = isVisible ? (isDimmed ? DIMMED_OPACITY : 1) : 0;

            return {
                id: relationship.id,
                source: relationship.source,
                sourceHandle: relationship.sourceHandle,
                target: relationship.target,
                targetHandle: relationship.targetHandle,
                data: {
                    branchId: relationship.branchId,
                    dependencyType: relationship.dependencyType,
                    isVisible,
                    opacity,
                    state: relationship.state,
                    transferredData: relationship.transferredData,
                },
                label: isDependency && isVisible ? t('shared data') : undefined,
                labelBgStyle: { fill: '#ffffff' },
                labelStyle: { fontSize: 10, fill: '#74788d' },
                style: {
                    opacity,
                    stroke: 'transparent',
                    strokeWidth: BASE_EDGE.strokeWidth,
                    transition: TRANSITION,
                },
            };
        })
    ), [
        branchIdByNodeId,
        hoveredBranchId,
        isBranchExpanded,
        isFinished,
        isRunning,
        relationships,
        t,
    ]);

    // Signals ride the same edges the canvas draws, hidden ones stay silent.
    const signalEdges = useMemo(() => (
        flowEdges.map((edge) => ({
            id: edge.id,
            baseStroke: edge.data.dependencyType === 'data-dependency' ? '#9aa6e0' : BASE_EDGE.stroke,
            isVisible: edge.data.isVisible,
            opacity: edge.data.opacity,
            source: edge.source,
            sourceHandle: edge.sourceHandle,
            target: edge.target,
            targetHandle: edge.targetHandle,
        }))
    ), [flowEdges]);

    const handleNodeMouseEnter = useCallback((_event, node) => {
        if (node.data.branchId) {
            setHoveredBranchId(node.data.branchId);
        }
    }, []);

    const handleNodeMouseLeave = useCallback(() => {
        setHoveredBranchId(null);
    }, []);

    const controls = (
        <div className={`d-flex flex-wrap align-items-start gap-2 mb-3 ${showHeader ? 'justify-content-between' : 'justify-content-end'}`}>
            {showHeader && (
                <div>
                    <h5 className="mb-1">{t('Live Verification Flow')}</h5>
                    <p className="text-muted mb-0">
                        {t('Hover a verification branch to follow its checks through the graph.')}
                    </p>
                </div>
            )}

            <div className="d-flex align-items-center gap-2">
                <Button
                    color="secondary"
                    onClick={() => reactFlowInstance.fitView({ padding: 0.1 })}
                    outline
                    size="sm"
                    type="button"
                >
                    <i className="ri-focus-3-line align-middle me-1" />
                    {t('Fit View')}
                </Button>

                <Button
                    color="primary"
                    disabled={isRunning}
                    onClick={onStart}
                    outline
                    size="sm"
                    type="button"
                >
                    <i className="ri-play-circle-line align-middle me-1" />
                    {t(startLabels[status] || 'Start')}
                </Button>

                <Button
                    color="secondary"
                    disabled={!isRunning}
                    onClick={onPause}
                    outline
                    size="sm"
                    type="button"
                >
                    <i className="ri-pause-circle-line align-middle me-1" />
                    {t('Pause')}
                </Button>

                <Button
                    color="secondary"
                    disabled={status === SIMULATION_STATUS.idle}
                    onClick={onReset}
                    outline
                    size="sm"
                    type="button"
                >
                    <i className="ri-restart-line align-middle me-1" />
                    {t('Reset')}
                </Button>
            </div>
        </div>
    );

    const canvas = (
        <div className={showHeader ? 'border rounded' : 'live-verification-flow'} style={{ height: 660 }}>
            <ReactFlow
                edges={flowEdges}
                elementsSelectable={false}
                fitView
                fitViewOptions={{ padding: 0.1 }}
                minZoom={0.3}
                nodes={flowNodes}
                nodesConnectable={false}
                nodesDraggable={false}
                nodeTypes={nodeTypes}
                onNodeMouseEnter={handleNodeMouseEnter}
                onNodeMouseLeave={handleNodeMouseLeave}
            >
                <VerificationSignals
                    edges={signalEdges}
                    getEdgeTrails={getEdgeTrails}
                    isRunning={isRunning}
                />

                <Controls showInteractive={false} />
            </ReactFlow>
        </div>
    );

    if (!showHeader) {
        return (
            <div>
                {controls}
                {canvas}
            </div>
        );
    }

    return (
        <Card>
            <CardBody>
                {controls}

                <div className="d-flex flex-wrap gap-3 mb-3">
                    {FLOW_STATE_LEGEND.map((state) => {
                        const visuals = getStateVisuals(state);

                        return (
                            <span className="font-size-11 text-muted" key={state}>
                                <i className={`${visuals.icon} text-${visuals.color} align-middle me-1`} />
                                {t(visuals.label)}
                            </span>
                        );
                    })}
                </div>

                {canvas}
            </CardBody>
        </Card>
    );
};

const TranslatedBidVerificationFlowCanvas = withNamespaces()(BidVerificationFlowCanvas);

const BidVerificationFlow = (props) => (
    <ReactFlowProvider>
        <TranslatedBidVerificationFlowCanvas {...props} />
    </ReactFlowProvider>
);

export default BidVerificationFlow;
